import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";

const defaultContent = {
  topTitle: "My Love, My Story",
  topText:
    "Welcome to Studio Art! We believe every love story is unique and deserves to be told beautifully. Our passion is capturing your most cherished moments with creativity and heart.",
  topImg: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308",
  aboutText1:
    "Studio Art is a young and dynamic multimedia studio specializing in Ad film making, corporate visual making, wedding cinematic videography, and candid photography in Madurai, Trichy, Chennai, Salem, Coimbatore, Karur, Pondicherry, Bangalore.",
  aboutText2:
    "We offer Photography, Cinematography, and Contemporary Photography services. Our team loves capturing wonderful moments on your wedding day and blending them with creative ideas and cinematography technology to deliver memories that become the highlight of your event.",
  aboutImg: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca",
  midImg: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
  midTitle: "Our Creative Vision",
  midText:
    "At Studio Art, we believe every moment is a masterpiece waiting to be captured. Our creative team blends technical expertise with artistic flair, ensuring your memories are preserved in the most beautiful way possible. Whether it’s a candid smile or a grand celebration, we turn your story into timeless art.",
  ctaTitle: "Let's Capture Your Story Together!",
  ctaText:
    "Reach out to us for a personalized session and let’s make your moments unforgettable.",
};

const AboutPage = () => {
  const [content, setContent] = useState(defaultContent);

  // Dialog state
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editImgFile, setEditImgFile] = useState(null);

  const clientMongoId = localStorage.getItem("clientMongoId");

  // Open edit dialog
  const handleEdit = (key, value) => {
    setEditKey(key);
    setEditValue(value);
    setEditImgFile(null);
  };

  // Save changes
  const saveAboutPageToDB = async (aboutPage) => {
    try {
      await axios.put(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/about-page`,
        { aboutPage }
      );
    } catch (err) {
      alert("Failed to save AboutPage content.");
    }
  };

  const handleSave = async () => {
    if (editKey.endsWith("Img") && editImgFile) {
      // If editing image, convert to base64
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const updated = { ...content, [editKey]: ev.target.result };
        setContent(updated);
        setEditKey(null);
        await saveAboutPageToDB(updated);
      };
      reader.readAsDataURL(editImgFile);
    } else {
      const updated = { ...content, [editKey]: editValue };
      setContent(updated);
      setEditKey(null);
      await saveAboutPageToDB(updated);
    }
  };

  useEffect(() => {
    async function fetchAboutPage() {
      if (!clientMongoId) return;
      try {
        const res = await axios.get(
          `https://studio-lc.onrender.com/api/clients/${clientMongoId}/about-page`
        );
        if (res.data.aboutPage) setContent(res.data.aboutPage);
      } catch (err) {
        setContent(defaultContent);
      }
    }
    fetchAboutPage();
  }, [clientMongoId]);

  return (
    <Box sx={{ bgcolor: "#f9f7f3", minHeight: "100vh" }}>
      {/* Top Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // Stack on mobile
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#e7dfd5",
          py: { xs: 3, md: 6 },
          px: { xs: 1, md: 4 },
        }}
      >
        {/* Left Text */}
        <Box
          sx={{
            flex: 1,
            textAlign: "left",
            pr: { xs: 0, md: 4 },
            pb: { xs: 2, md: 0 },
            width: "100%",
            position: "relative",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontFamily:"Josefin Sans",
              mb: 2,
              color: "#8b5e3c",
              letterSpacing: 2,
              fontSize: { xs: 28, sm: 32, md: 40 },
              wordBreak: "break-word",
            }}
          >
            {content.topTitle}
            <IconButton
              sx={{ ml: 2 }}
              onClick={() => handleEdit("topTitle", content.topTitle)}
            >
              <EditIcon />
            </IconButton>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              fontFamily:"Jost",
              color: "#5c4032",
              mb: 3,
              lineHeight: 1.6,
              maxWidth: { xs: "100%", md: 1000 },
              fontSize: { xs: 16, sm: 18, md: 22 },
              wordBreak: "break-word",
            }}
          >
            {content.topText}
            <IconButton
              sx={{ ml: 2 }}
              onClick={() => handleEdit("topText", content.topText)}
            >
              <EditIcon />
            </IconButton>
          </Typography>
        </Box>
        {/* Right Portrait Image */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: { xs: 180, md: 340 },
            width: { xs: "100%", sm: 240, md: 520 },
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: 4,
            ml: { xs: 0, md: 4 },
            mr: { xs: 0, md: 10 },
            bgcolor: "#fff",
            position: "relative",
            mt: { xs: 2, md: 0 },
          }}
        >
          <img
            src={content.topImg}
            alt="Photographer"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "16px",
            }}
          />
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }}
            onClick={() => handleEdit("topImg", content.topImg)}
          >
            <EditIcon />
          </IconButton>
        </Box>
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Text */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                mb: 3,
                fontSize: { xs: 15, sm: 17, md: 18 },
              fontFamily:"Jost",
                color: "#5c4032",
                maxWidth: 600,
                wordBreak: "break-word",
              }}
            >
              {content.aboutText1}
              <IconButton
                sx={{ ml: 2 }}
                onClick={() => handleEdit("aboutText1", content.aboutText1)}
              >
                <EditIcon />
              </IconButton>
              <br />
              <br />
              <span style={{ fontWeight: 600, color: "#8b5e3c",fontFamily:"Josefin Sans",  }}>
                Studio Art is one of the Best Wedding Photography studios in
                Madurai.
              </span>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                fontSize: { xs: 15, sm: 17, md: 18 },
                fontFamily: "jost",
                color: "#5c4032",
                maxWidth: 600,
                wordBreak: "break-word",
              }}
            >
              {content.aboutText2}
              <IconButton
                sx={{ ml: 2 }}
                onClick={() => handleEdit("aboutText2", content.aboutText2)}
              >
                <EditIcon />
              </IconButton>
            </Typography>
          </Grid>
          {/* Right Image */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={content.aboutImg}
                alt="Wedding Couple"
                sx={{
                  width: "100%",
                  height: { xs: 180, md: 340 },
                  objectFit: "cover",
                  borderRadius: 4,
                  boxShadow: 3,
                }}
              />
              <IconButton
                sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }}
                onClick={() => handleEdit("aboutImg", content.aboutImg)}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Mid Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Portrait Image */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: 180, md: 340 },
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: 3,
                bgcolor: "#fff",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={content.midImg}
                alt="Photography"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
              <IconButton
                sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }}
                onClick={() => handleEdit("midImg", content.midImg)}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: { xs: 1, md: 4 },
                minHeight: { xs: 120, md: 340 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontFamily:"Josefin Sans", 
                  mb: 2,
                  letterSpacing: 1,
                  color: "#8b5e3c",
                  fontSize: { xs: 22, sm: 26, md: 32 },
                }}
              >
                {content.midTitle}
                <IconButton
                  sx={{ ml: 2 }}
                  onClick={() => handleEdit("midTitle", content.midTitle)}
                >
                  <EditIcon />
                </IconButton>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "jost",
                  fontSize: { xs: 15, sm: 17, md: 18 },
                  lineHeight: 1.8,
                  color: "#5c4032",
                  wordBreak: "break-word",
                }}
              >
                {content.midText}
                <IconButton
                  sx={{ ml: 2 }}
                  onClick={() => handleEdit("midText", content.midText)}
                >
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: "#e7dfd5",
          py: { xs: 3, md: 6 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontFamily:"Josefin Sans", 
            color: "#8b5e3c",
            mb: 2,
            letterSpacing: 1,
            fontSize: { xs: 22, sm: 26, md: 32 },
          }}
        >
          {content.ctaTitle}
          <IconButton
            sx={{ ml: 2 }}
            onClick={() => handleEdit("ctaTitle", content.ctaTitle)}
          >
            <EditIcon />
          </IconButton>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "jost",
            fontSize: { xs: 15, sm: 17, md: 18 },
            color: "#5c4032",
            mb: 3,
            wordBreak: "break-word",
          }}
        >
          {content.ctaText}
          <IconButton
            sx={{ ml: 2 }}
            onClick={() => handleEdit("ctaText", content.ctaText)}
          >
            <EditIcon />
          </IconButton>
        </Typography>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={!!editKey}
        onClose={() => setEditKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit{" "}
          {editKey &&
            editKey
              .replace(/([A-Z])/g, " $1")
              .replace("Img", " Image")}
        </DialogTitle>
        <DialogContent>
          {editKey && editKey.endsWith("Img") ? (
            <>
              <Box sx={{ mb: 2 }}>
                <img
                  src={editValue}
                  alt="Edit"
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    height: 180,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
              </Box>
              <Button variant="contained" component="label">
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setEditImgFile(e.target.files[0])}
                />
              </Button>
            </>
          ) : (
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ fontFamily:"Josefin Sans", }} onClick={() => setEditKey(null)}>Cancel</Button>
          <Button sx={{ fontFamily:"Josefin Sans", }} variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AboutPage;
